import { Button } from "@/components/ui/button";
import * as Sentry from "@sentry/react";
import { AssessmentQuestion } from "./questionTypes";
import { AnswerData, isRadioAnswer, isCheckboxAnswer, isSliderAnswer, isTextAnswer, isSelectAnswer, isSuccessorFormAnswer, isDateSliderAnswer, isMultiFieldTextAnswer, isAddressAnswer, isDynamicRepeatingAnswer, isFileUploadAnswer, QuestionAnswer } from "./answerTypes";
import { otherTextKey } from "./OtherTextInput";
import { WelcomeScreen } from "./WelcomeScreen";
import { LevelSetScreen } from "./LevelSetScreen";
import { CompletionScreen } from "./CompletionScreen";
import { OwnerReadinessCompletionScreen } from "./OwnerReadinessCompletionScreen";
import { BusinessProfileCompletionScreen } from "./BusinessProfileCompletionScreen";
import { MySuccessionCompletionScreen } from "./MySuccessionCompletionScreen";
import { DbAssessmentCompletionScreen } from "./DbAssessmentCompletionScreen";
import { RadioQuestion } from "./RadioQuestion";
import { CheckboxQuestion } from "./CheckboxQuestion";
import { SliderQuestion } from "./SliderQuestion";
import { StarRatingQuestion } from "./StarRatingQuestion";
import { TextQuestion } from "./TextQuestion";
import { SelectQuestion } from "./SelectQuestion";
import { HeaderQuestion } from "./HeaderQuestion";
import { SuccessorFormQuestionComponent } from "./SuccessorFormQuestion";
import { DateSliderQuestion } from "./DateSliderQuestion";
import { MultiFieldTextQuestionComponent } from "./MultiFieldTextQuestion";
import { AddressQuestionComponent } from "./AddressQuestion";
import { DynamicRepeatingQuestionComponent } from "./DynamicRepeatingQuestion";
import { logger } from '@/utils/logger';
import { FileUploadQuestionComponent } from "./FileUploadQuestion";
import { FileUploadAdvanced } from "./FileUploadAdvanced";
import { BusinessDetailsSummaryQuestion } from "./BusinessDetailsSummaryQuestion";
import { YearCalculator } from "./YearCalculator";
import { YearPickerQuestion } from "./YearPickerQuestion";
import { MonthSelectorQuestion } from "./MonthSelectorQuestion";
import { NumberQuestion } from "./NumberQuestion";
import { RepeatingGroupQuestion } from "./RepeatingGroupQuestion";
import { RankingQuestion, RankingItem } from "./RankingQuestion";
import { AllocationInputQuestion } from "./AllocationInputQuestion";


interface QuestionRendererProps {
  question: AssessmentQuestion;
  answers: AnswerData;
  onAnswerChange: (questionId: string, value: QuestionAnswer) => void;
  onNext: () => void;
  onFinish: () => void;
  onNextAssessment?: () => void;
}

const GOVERNANCE_MECHANISM_LABELS: Record<string, string> = {
  board_advisors: "Board of advisors or directors",
  shareholder_agreement: "Shareholders agreement",
  buy_sell_agreement: "Buy-sell agreement",
  family_council: "Family council or governance charter",
  operating_agreement: "Operating or partnership agreement",
  succession_plan_written: "Written succession plan document",
};

function GovernanceDocumentContext({ answers }: { answers: AnswerData }) {
  const selected = answers["governance_mechanisms"];
  if (!selected || !Array.isArray(selected) || selected.length === 0) return null;

  const labels = selected
    .map((v: string) => GOVERNANCE_MECHANISM_LABELS[v])
    .filter(Boolean);

  if (labels.length === 0) return null;

  const singular = labels.length === 1;

  return (
    <div className="space-y-2 mb-6">
      <p className="text-base text-muted-foreground">
        {singular
          ? "When was the following last reviewed and updated?"
          : "When were each of the following last reviewed and updated?"}
      </p>
      <ul className="list-disc list-inside space-y-1 text-sm text-foreground/80">
        {labels.map((label: string) => (
          <li key={label}>{label}</li>
        ))}
      </ul>
    </div>
  );
}

function QuestionHeader({ question, answers }: { question: AssessmentQuestion; answers: AnswerData }) {
  return (
    <div>
      {/* Years in business auto-calculation for company timeline */}
      {question.id === "company-timeline" && answers[question.id] && (
        <YearCalculator foundedYear={answers[question.id] as string} className="mb-4" />
      )}
      {/* Context-aware document list for governance_document_currency */}
      {question.id === "governance_document_currency" && (
        <GovernanceDocumentContext answers={answers} />
      )}
    </div>
  );
}

export function QuestionRenderer({ 
  question, 
  answers, 
  onAnswerChange, 
  onNext, 
  onFinish,
  onNextAssessment
}: QuestionRendererProps) {
  
  // Add debugging for question rendering
  logger.component('QuestionRenderer', 'rendering', {
    questionId: question.id,
    questionType: question.type,
    questionTitle: question.title,
    hasAnswer: !!answers[question.id]
  });
  
  // For content questions, wrap with header
  const renderWithHeader = (content: React.ReactNode) => (
    <div className="space-y-6">
      <QuestionHeader question={question} answers={answers} />
      {content}
    </div>
  );

  switch (question.type) {
    case 'welcome':
      return <WelcomeScreen question={question} onNext={onNext} />;
        
    case 'level-set':
      return <LevelSetScreen question={question} onNext={onNext} />;

    case 'recall-bridge':
      // Handled at parent level in ConversationalAssessment
      return null;
        
    case 'completion':
      // Different completion screens based on assessment context
      if (question.id.includes('owner-readiness')) {
        return <OwnerReadinessCompletionScreen question={question} onFinish={onFinish} onNextAssessment={onNextAssessment} />;
      } else if (question.id.includes('business-profile')) {
        return <BusinessProfileCompletionScreen question={question} onFinish={onFinish} onNextAssessment={onNextAssessment} />;
      } else if (question.id.includes('snapshot') || question.id.includes('succession')) {
        return <MySuccessionCompletionScreen question={question} onFinish={onFinish} answers={answers} onNextAssessment={onNextAssessment} />;
      }
      // Generic completion for DB-driven assessments
      if (question.id.endsWith('-completion')) {
        const moduleId = question.id.replace(/-completion$/, '');
        return <DbAssessmentCompletionScreen question={question} onFinish={onFinish} moduleId={moduleId} onNextAssessment={onNextAssessment} />;
      }
      return <CompletionScreen question={question} onFinish={onFinish} onNextAssessment={onNextAssessment} />;
        
    case 'radio':
      const radioValue = answers[question.id];
      const radioOtherText = answers[otherTextKey(question.id)];
      return renderWithHeader(
        <RadioQuestion
          question={question}
          value={isRadioAnswer(radioValue) ? radioValue : ""}
          otherText={typeof radioOtherText === 'string' ? radioOtherText : ""}
          onChange={onAnswerChange}
          allAnswers={answers}
        />
      );
        
    case 'checkbox':
      const checkboxValue = answers[question.id];
      const checkboxOtherText = answers[otherTextKey(question.id)];
      return renderWithHeader(
        <CheckboxQuestion
          question={question}
          values={isCheckboxAnswer(checkboxValue) ? checkboxValue : []}
          otherText={typeof checkboxOtherText === 'string' ? checkboxOtherText : ""}
          onChange={onAnswerChange}
        />
      );
        
    case 'slider':
      try {
      const sliderValue = answers[question.id];
        const sliderQuestion = question as import('./questionTypes').SliderQuestion;
        
        // Validate slider question has required properties
        if ((!sliderQuestion.labels && !sliderQuestion.stepLabels) || sliderQuestion.minValue === undefined || sliderQuestion.maxValue === undefined) {
          Sentry.captureException(new Error('Invalid slider question configuration'), {
            tags: { context: 'validation_error', component: 'QuestionRenderer' },
            extra: { message: 'Invalid slider question configuration', sliderQuestion }
          });
          return renderWithHeader(
            <div className="text-center p-4 border border-destructive/20 rounded-lg bg-destructive/5">
              <p className="text-destructive font-medium">Invalid slider configuration</p>
              <p className="text-sm text-muted-foreground mt-1">Question: {question.id}</p>
              <p className="text-xs text-muted-foreground mt-1">Missing required properties (labels, minValue, maxValue)</p>
            </div>
          );
        }

        // Star rating variant
        if (sliderQuestion.renderAs === 'star_rating') {
          return (
            <StarRatingQuestion
              question={sliderQuestion}
              value={isSliderAnswer(sliderValue) ? sliderValue : (sliderQuestion.defaultValue || sliderQuestion.minValue || 0)}
              onChange={onAnswerChange}
            />
          );
        }
        
        return (
          <SliderQuestion
            question={sliderQuestion}
            value={isSliderAnswer(sliderValue) ? sliderValue : (sliderQuestion.defaultValue || sliderQuestion.minValue || 0)}
            onChange={onAnswerChange}
          />
        );
      } catch (error) {
        Sentry.captureException(error, {
          tags: { context: 'render_error', component: 'QuestionRenderer' },
          extra: { message: 'Error rendering slider question', question }
        });
        return renderWithHeader(
          <div className="text-center p-4 border border-destructive/20 rounded-lg bg-destructive/5">
            <p className="text-destructive font-medium">Error rendering slider question</p>
            <p className="text-sm text-muted-foreground mt-1">Question: {question.id}</p>
          </div>
        );
      }

    case 'text':
      const textValue = answers[question.id];
      return renderWithHeader(
        <TextQuestion
          question={question}
          value={isTextAnswer(textValue) ? textValue : ""}
          onChange={onAnswerChange}
        />
      );

    case 'select':
      const selectValue = answers[question.id];
      return renderWithHeader(
        <SelectQuestion
          question={question}
          value={isSelectAnswer(selectValue) ? selectValue : undefined}
          onChange={onAnswerChange}
        />
      );

    case 'header':
      return <HeaderQuestion question={question} onNext={onNext} />;

    case 'successor-form':
      const successorValue = answers[question.id];
      return (
        <SuccessorFormQuestionComponent
          question={question}
          value={isSuccessorFormAnswer(successorValue) ? successorValue : {}}
          onChange={onAnswerChange}
        />
      );

    case 'date-slider':
      const dateSliderValue = answers[question.id];
      return (
        <DateSliderQuestion
          question={question}
          value={isDateSliderAnswer(dateSliderValue) ? dateSliderValue : undefined}
          onChange={onAnswerChange}
        />
      );

    case 'multi-field-text':
      const multiFieldValue = answers[question.id];
      
      // Use enhanced BusinessDetailsSummaryQuestion for business-details
      if (question.id === 'business-details') {
        return (
          <BusinessDetailsSummaryQuestion
            question={question}
            values={isMultiFieldTextAnswer(multiFieldValue) ? multiFieldValue : {}}
            onChange={onAnswerChange}
          />
        );
      }
      
      // Use default component for other multi-field-text questions
      return (
        <MultiFieldTextQuestionComponent
          question={question}
          values={isMultiFieldTextAnswer(multiFieldValue) ? multiFieldValue : {}}
          onChange={onAnswerChange}
        />
      );

    case 'address':
      const addressValue = answers[question.id];
      return (
        <AddressQuestionComponent
          question={question}
          values={isAddressAnswer(addressValue) ? addressValue as Record<string, string> : {}}
          onChange={onAnswerChange}
        />
      );

    case 'dynamic-repeating':
      const dynamicValue = answers[question.id];
      return (
        <DynamicRepeatingQuestionComponent
          question={question}
          values={isDynamicRepeatingAnswer(dynamicValue) ? dynamicValue.entries : []}
          onChange={(questionId, entries) => onAnswerChange(questionId, { entries })}
        />
      );

    case 'file-upload':
      const fileValue = answers[question.id];
      // Use advanced component for multi-file uploads
      if (question.allowMultiple) {
        return (
          <FileUploadAdvanced
            question={question}
            values={isFileUploadAnswer(fileValue) ? fileValue : null}
            onChange={onAnswerChange}
          />
        );
      }
      return (
        <FileUploadQuestionComponent
          question={question}
          value={isFileUploadAnswer(fileValue) ? fileValue : null}
          onChange={onAnswerChange}
        />
      );
        
    case 'year-picker': {
      const ypValue = answers[question.id];
      return renderWithHeader(
        <YearPickerQuestion
          question={question}
          value={isTextAnswer(ypValue) ? ypValue : ""}
          onChange={onAnswerChange}
        />
      );
    }

    case 'month-selector': {
      const msValue = answers[question.id];
      return renderWithHeader(
        <MonthSelectorQuestion
          question={question}
          value={isTextAnswer(msValue) ? msValue : ""}
          onChange={onAnswerChange}
        />
      );
    }

    case 'number-input': {
      const numValue = answers[question.id];
      return renderWithHeader(
        <NumberQuestion
          question={question}
          value={isTextAnswer(numValue) ? numValue : ""}
          onChange={onAnswerChange}
        />
      );
    }

    case 'repeating-group': {
      const rgValue = answers[question.id];
      return renderWithHeader(
        <RepeatingGroupQuestion
          question={question}
          value={isTextAnswer(rgValue) ? rgValue : "[]"}
          onChange={onAnswerChange}
        />
      );
    }

    case 'ranking': {
      const rankingQuestion = question as import('./questionTypes').RankingQuestion;
      const rankingValue = answers[question.id];
      // Parse stored value (JSON array of RankingItem) or use empty for init
      let parsedRanking: RankingItem[] = [];
      if (Array.isArray(rankingValue)) {
        parsedRanking = rankingValue as unknown as RankingItem[];
      } else if (typeof rankingValue === 'string') {
        try { parsedRanking = JSON.parse(rankingValue); } catch { parsedRanking = []; }
      }
      return renderWithHeader(
        <RankingQuestion
          question={rankingQuestion}
          value={parsedRanking}
          onChange={(qId, ranked) => onAnswerChange(qId, ranked as unknown as QuestionAnswer)}
        />
      );
    }

    case 'allocation_input': {
      const allocValue = answers[question.id];
      const allocObj = (allocValue && typeof allocValue === 'object' && !Array.isArray(allocValue))
        ? allocValue as Record<string, number>
        : {};
      return renderWithHeader(
        <AllocationInputQuestion
          question={question}
          value={allocObj}
          onChange={onAnswerChange}
        />
      );
    }

    default:
      return <div>Unsupported question type</div>;
  }
}
